import { createHarvestsApi } from "@common/api/harvests";
import type { HttpTransport } from "@common/api/transport";

import { createAiGenerationsApi } from "./aiGenerations";
import { createColorsApi } from "./colors";
import { createCropsApi } from "./crops";
import { createCropSchedulesApi } from "./cropSchedules";
import { createCropValuesApi } from "./cropValues";
import { createCurrenciesApi } from "./currencies";
import { createFirstPromoterApi } from "./firstPromoter";
import { createFrostEstimationsApi } from "./frostEstimations";
import { createGardenCropsApi } from "./gardenCrops";
import { createGardensApi } from "./gardens";
import { createInventoryApi } from "./inventory";
import { createNotesApi } from "./notes";
import { createPlantingsApi } from "./plantings";
import { createProductsApi } from "./products";
import { createScheduleRecommendationsApi } from "./scheduleRecommendations";
import { createSettingsApi } from "./settings";
import { createSubscriptionApi } from "./subscription";
import { createTagsApi } from "./tags";
import { createTasksApi } from "./tasks";
import { createUserApi } from "./user";
import { createWorkflowTemplatesApi } from "./workflowTemplates";

/**
 * Composes the feature modules into a single namespaced client bound to a
 * transport. Each platform provides its own transport and constructs its own
 * client: web in `app/frontend/apiClient.ts` (fetchTransport), mobile with an
 * axios/Bearer transport. This module is platform-neutral and mirror-able.
 */
export const createApiClient = (http: HttpTransport) => ({
  aiGenerations: createAiGenerationsApi(http),
  colors: createColorsApi(http),
  crops: createCropsApi(http),
  cropSchedules: createCropSchedulesApi(http),
  cropValues: createCropValuesApi(http),
  currencies: createCurrenciesApi(http),
  firstPromoter: createFirstPromoterApi(http),
  frostEstimations: createFrostEstimationsApi(http),
  gardenCrops: createGardenCropsApi(http),
  gardens: createGardensApi(http),
  harvests: createHarvestsApi(http),
  inventory: createInventoryApi(http),
  notes: createNotesApi(http),
  plantings: createPlantingsApi(http),
  products: createProductsApi(http),
  scheduleRecommendations: createScheduleRecommendationsApi(http),
  settings: createSettingsApi(http),
  subscription: createSubscriptionApi(http),
  tags: createTagsApi(http),
  tasks: createTasksApi(http),
  user: createUserApi(http),
  workflowTemplates: createWorkflowTemplatesApi(http)
});

export type ApiClient = ReturnType<typeof createApiClient>;

// Co-located request/response types (erased at runtime — no barrel getters).
export type { CropValuesParams } from "./cropValues";
export type { FetchCropSchedulesParams } from "./cropSchedules";
export type { FrostZoneParams, FrostZoneResponse } from "./settings";
export type { UpsertGardenCropParams } from "./gardenCrops";
export type { CompleteTaskParams, MoveTaskParams } from "./tasks";
export type {
  PlantingWindow,
  PlantingWindowMarker,
  PlantingWindowMarkerGate,
  PlantingWindowSowDate,
  PlantingWindowsResponse,
  RecommendedTask,
  ScheduleRecommendationParams
} from "./scheduleRecommendations";
